import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, Clock, Target, LineChart, RefreshCw, TrendingUp, TrendingDown, Calculator, Ruler, GitCompare, Zap } from 'lucide-react';
import api from '../lib/api';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input, Textarea } from '../components/ui/Input';
import { Spinner } from '../components/ui/Spinner';
import { fmtMinutes, timeAgo } from '../lib/utils';

/* ---------- shared field ---------- */
function Field({ label, prefix, suffix, children }) {
  return (
    <div className="flex-1">
      <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-zinc-500">{label}</label>
      <div className="flex items-center gap-1.5 rounded-xl border border-zinc-800 bg-ink-surface px-3 py-1.5">
        {prefix && <span className="text-sm text-zinc-500">{prefix}</span>}
        {children}
        {suffix && <span className="text-sm text-zinc-500">{suffix}</span>}
      </div>
    </div>
  );
}
const numInput = 'w-full bg-transparent text-lg font-bold text-white outline-none placeholder:text-zinc-600';
const clean = (v) => v.replace(/[^0-9.]/g, '');

/* ================= EV CALCULATOR ================= */
function evColor(ev) {
  if (ev < 0.15) return { color: '#F97066', label: 'Low edge' };
  if (ev < 0.4) return { color: '#F59E0B', label: 'Acceptable' };
  if (ev <= 0.6) return { color: '#39FF14', label: 'Strong edge' };
  return { color: '#8B5CF6', label: 'Elite edge' };
}
function EVCalculator() {
  const [winRate, setWinRate] = useState('55');
  const [rr, setRr] = useState('2');
  const ev = useMemo(() => {
    const wr = Math.max(0, Math.min(100, parseFloat(winRate || '0'))) / 100;
    const r = Math.max(0, parseFloat(rr || '0'));
    return wr * r - (1 - wr);
  }, [winRate, rr]);
  const meta = evColor(ev);
  return (
    <Card className="mx-auto max-w-xl p-6" data-testid="ev-calc">
      <div className="flex gap-4">
        <Field label="Win Rate" suffix="%"><input data-testid="ev-wr" inputMode="decimal" className={numInput} value={winRate} onChange={(e) => setWinRate(clean(e.target.value))} /></Field>
        <Field label="Risk : Reward" suffix="R"><input data-testid="ev-rr" inputMode="decimal" className={numInput} value={rr} onChange={(e) => setRr(clean(e.target.value))} /></Field>
      </div>
      <div className="mt-5 rounded-2xl border p-6 text-center" style={{ borderColor: meta.color, background: `${meta.color}18` }}>
        <div className="text-[11px] font-bold uppercase tracking-widest text-zinc-400">Expected Value</div>
        <div className="tnum text-5xl font-black" style={{ color: meta.color }} data-testid="ev-result">{ev.toFixed(2)}</div>
        <div className="text-xs font-bold" style={{ color: meta.color }}>{meta.label}</div>
      </div>
      <p className="mt-4 text-xs leading-relaxed text-zinc-500">Formula: EV = (WinRate × RR) − (1 − WinRate). Anything &gt; 0.15 is worth taking; over 0.40 is a genuinely strong setup.</p>
    </Card>
  );
}

/* ================= POSITION SIZE CALCULATOR ================= */
function money(n) { return `$${(Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`; }
function PositionSizeCalculator() {
  const [kind, setKind] = useState('options');
  const [account, setAccount] = useState('25000');
  const [riskPct, setRiskPct] = useState('1');
  const [entry, setEntry] = useState('2.50');
  const [stopPct, setStopPct] = useState('50');
  const [stopDistance, setStopDistance] = useState('2.00');
  const [tickValue, setTickValue] = useState('12.50');
  const [tickSize, setTickSize] = useState('0.25');

  const r = useMemo(() => {
    const acct = Math.max(0, parseFloat(account || '0'));
    const rPct = Math.max(0, parseFloat(riskPct || '0'));
    const dollarsAtRisk = (acct * rPct) / 100;
    if (kind === 'options') {
      const premium = Math.max(0, parseFloat(entry || '0'));
      const sPct = Math.max(0, parseFloat(stopPct || '0')) / 100;
      const costPerContract = premium * 100;
      const lossPerContract = costPerContract * sPct;
      const contracts = lossPerContract > 0 ? Math.floor(dollarsAtRisk / lossPerContract) : 0;
      return { kind, dollarsAtRisk, contracts, costPerContract, lossPerContract, positionCost: contracts * costPerContract, maxLoss: contracts * lossPerContract };
    }
    const stop = Math.max(0, parseFloat(stopDistance || '0'));
    const tv = Math.max(0, parseFloat(tickValue || '0'));
    const ts = Math.max(0.000001, parseFloat(tickSize || '0.25'));
    const stopTicks = stop / ts;
    const riskPerContract = stopTicks * tv;
    const contracts = riskPerContract > 0 ? Math.floor(dollarsAtRisk / riskPerContract) : 0;
    return { kind, dollarsAtRisk, contracts, riskPerContract, stopTicks };
  }, [kind, account, riskPct, entry, stopPct, stopDistance, tickValue, tickSize]);

  return (
    <Card className="mx-auto max-w-xl p-6" data-testid="pos-calc">
      <div className="mb-4 flex rounded-xl border border-zinc-800 bg-ink-surface p-1">
        {['options', 'futures'].map((k) => (
          <button key={k} onClick={() => setKind(k)} data-testid={`pos-kind-${k}`}
            className={`flex-1 rounded-lg py-2 text-sm font-medium capitalize transition-colors ${kind === k ? 'bg-white text-black' : 'text-zinc-400'}`}>{k}</button>
        ))}
      </div>
      <div className="flex gap-4">
        <Field label="Account Size" prefix="$"><input data-testid="pos-account" inputMode="decimal" className={numInput} value={account} onChange={(e) => setAccount(clean(e.target.value))} /></Field>
        <Field label="Risk per Trade" suffix="%"><input data-testid="pos-risk" inputMode="decimal" className={numInput} value={riskPct} onChange={(e) => setRiskPct(clean(e.target.value))} /></Field>
      </div>
      {kind === 'options' ? (
        <div className="mt-4 flex gap-4">
          <Field label="Entry Premium" prefix="$"><input data-testid="pos-entry" inputMode="decimal" className={numInput} value={entry} onChange={(e) => setEntry(clean(e.target.value))} /></Field>
          <Field label="Stop Loss" suffix="%"><input data-testid="pos-stoppct" inputMode="decimal" className={numInput} value={stopPct} onChange={(e) => setStopPct(clean(e.target.value))} /></Field>
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-3 gap-4">
          <Field label="Stop Dist" suffix="pts"><input data-testid="pos-stop" inputMode="decimal" className={numInput} value={stopDistance} onChange={(e) => setStopDistance(clean(e.target.value))} /></Field>
          <Field label="Tick Size" suffix="pts"><input data-testid="pos-ticksize" inputMode="decimal" className={numInput} value={tickSize} onChange={(e) => setTickSize(clean(e.target.value))} /></Field>
          <Field label="Tick Value" prefix="$"><input data-testid="pos-tickvalue" inputMode="decimal" className={numInput} value={tickValue} onChange={(e) => setTickValue(clean(e.target.value))} /></Field>
        </div>
      )}
      <div className="mt-5 rounded-2xl border border-emerald/40 bg-emerald/10 p-6 text-center">
        <div className="text-[11px] font-bold uppercase tracking-widest text-zinc-400">Position Size</div>
        <div className="tnum text-5xl font-black text-emerald" data-testid="pos-result">{r.contracts}</div>
        <div className="text-xs font-bold text-emerald">contracts</div>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
        <div className="rounded-xl border border-white/5 bg-ink-surface p-3"><div className="text-xs text-zinc-500">$ at risk</div><div className="tnum font-bold text-white">{money(r.dollarsAtRisk)}</div></div>
        {kind === 'options' ? (
          <>
            <div className="rounded-xl border border-white/5 bg-ink-surface p-3"><div className="text-xs text-zinc-500">Cost / contract</div><div className="tnum font-bold text-white">{money(r.costPerContract)}</div></div>
            <div className="rounded-xl border border-white/5 bg-ink-surface p-3"><div className="text-xs text-zinc-500">Position cost</div><div className="tnum font-bold text-white">{money(r.positionCost)}</div></div>
            <div className="rounded-xl border border-white/5 bg-ink-surface p-3"><div className="text-xs text-zinc-500">Max loss</div><div className="tnum font-bold text-red-400">{money(r.maxLoss)}</div></div>
          </>
        ) : (
          <>
            <div className="rounded-xl border border-white/5 bg-ink-surface p-3"><div className="text-xs text-zinc-500">Stop ticks</div><div className="tnum font-bold text-white">{(r.stopTicks || 0).toFixed(1)}</div></div>
            <div className="rounded-xl border border-white/5 bg-ink-surface p-3"><div className="text-xs text-zinc-500">Risk / contract</div><div className="tnum font-bold text-white">{money(r.riskPerContract)}</div></div>
          </>
        )}
      </div>
    </Card>
  );
}

/* ================= STRATEGY COMPARATOR ================= */
function evPerTrade(wr, rr) { const p = Math.max(0, Math.min(1, wr / 100)); return p * rr - (1 - p); }
function equityCurve(s, trades, riskPct, start = 1000) {
  const p = Math.max(0, Math.min(1, s.winRate / 100));
  const r = Math.max(0, s.rr);
  const growth = 1 + (riskPct / 100) * (p * r - (1 - p));
  const out = new Array(trades + 1);
  let cap = start;
  for (let t = 0; t <= trades; t++) { out[t] = cap; cap *= growth; }
  return out;
}
function fmtMoney(n) {
  if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (Math.abs(n) >= 1e3) return `$${(n / 1e3).toFixed(1)}k`;
  return `$${n.toFixed(0)}`;
}
function EquityChart({ curveA, curveB, colorA, colorB }) {
  const w = 640, h = 240, padL = 50, padR = 14, padT = 20, padB = 26;
  const n = curveA.length;
  const maxY = Math.max(...curveA, ...curveB, 1);
  const minY = Math.min(...curveA, ...curveB, 0);
  const spanY = Math.max(1, maxY - minY);
  const spanX = Math.max(1, n - 1);
  const xAt = (i) => padL + (i / spanX) * (w - padL - padR);
  const yAt = (v) => padT + (1 - (v - minY) / spanY) * (h - padT - padB);
  const pts = (arr) => arr.map((v, i) => `${xAt(i).toFixed(1)},${yAt(v).toFixed(1)}`).join(' ');
  const ticks = 4;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full" data-testid="equity-chart">
      {Array.from({ length: ticks + 1 }, (_, i) => {
        const v = minY + (spanY * i) / ticks; const y = yAt(v);
        return (
          <g key={i}>
            <line x1={padL} y1={y} x2={w - padR} y2={y} stroke="#27272a" strokeWidth="1" />
            <text x={padL - 6} y={y + 3} textAnchor="end" fontSize="10" fill="#71717a" className="tnum">{fmtMoney(v)}</text>
          </g>
        );
      })}
      <polyline fill="none" stroke={colorA} strokeWidth="2.5" points={pts(curveA)} />
      <polyline fill="none" stroke={colorB} strokeWidth="2.5" points={pts(curveB)} />
    </svg>
  );
}
function StratField({ label, value, onChange, testid }) {
  return (
    <div className="flex-1">
      <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-zinc-500">{label}</label>
      <input data-testid={testid} inputMode="decimal" value={value} onChange={(e) => onChange(clean(e.target.value))}
        className="w-full rounded-xl border border-zinc-800 bg-ink-surface px-3 py-1.5 text-base font-bold text-white outline-none" />
    </div>
  );
}
function StrategyComparator() {
  const [a, setA] = useState({ name: '1st', color: '#3B82F6', winRate: 50, rr: 3 });
  const [b, setB] = useState({ name: '2nd', color: '#F97066', winRate: 60, rr: 2 });
  const [trades, setTrades] = useState('100');
  const [risk, setRisk] = useState('1');
  const n = Math.max(1, Math.min(1000, parseInt(trades || '0', 10) || 100));
  const riskPct = Math.max(0.1, Math.min(10, parseFloat(risk || '0') || 1));
  const curveA = useMemo(() => equityCurve(a, n, riskPct), [a, n, riskPct]);
  const curveB = useMemo(() => equityCurve(b, n, riskPct), [b, n, riskPct]);
  const rA = evPerTrade(a.winRate, a.rr) * n;
  const rB = evPerTrade(b.winRate, b.rr) * n;
  const winner = rA > rB + 0.001 ? 'A' : rB > rA + 0.001 ? 'B' : 'TIE';
  const Summary = ({ s, curve, r, win }) => (
    <div className="flex-1 rounded-2xl border p-4" style={{ borderColor: win ? s.color : 'rgba(255,255,255,0.06)' }} data-testid={`strat-sum-${s.name}`}>
      <div className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: s.color }} /><span className="text-sm text-zinc-300">{s.name}</span>{win && <span className="ml-auto text-[11px] font-bold" style={{ color: s.color }}>WINNER</span>}</div>
      <div className="tnum mt-2 text-3xl font-black" style={{ color: s.color }}>{fmtMoney(curve[curve.length - 1])}</div>
      <div className="tnum text-sm text-zinc-400">{r >= 0 ? '+' : ''}{r.toFixed(1)}R</div>
    </div>
  );
  return (
    <Card className="mx-auto max-w-2xl p-6" data-testid="strategy-compare">
      <div className="flex gap-4">
        {[{ s: a, set: setA, id: 'a' }, { s: b, set: setB, id: 'b' }].map(({ s, set, id }) => (
          <div key={id} className="flex-1 rounded-2xl border border-white/5 bg-ink-surface p-4">
            <div className="mb-3 flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: s.color }} /><span className="text-sm font-semibold text-white">Strategy {id.toUpperCase()}</span></div>
            <div className="space-y-3">
              <StratField label="Win Rate %" value={String(s.winRate)} onChange={(v) => set({ ...s, winRate: parseFloat(v) || 0 })} testid={`strat-${id}-wr`} />
              <StratField label="Risk : Reward" value={String(s.rr)} onChange={(v) => set({ ...s, rr: parseFloat(v) || 0 })} testid={`strat-${id}-rr`} />
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 flex gap-4">
        <StratField label="Trades" value={trades} onChange={(v) => setTrades(v.replace(/[^0-9]/g, ''))} testid="strat-trades" />
        <StratField label="Risk % / trade" value={risk} onChange={setRisk} testid="strat-risk" />
      </div>
      <div className="mt-5 rounded-2xl border border-white/5 bg-ink-surface p-4"><EquityChart curveA={curveA} curveB={curveB} colorA={a.color} colorB={b.color} /></div>
      <div className="mt-4 flex gap-4"><Summary s={a} curve={curveA} r={rA} win={winner === 'A'} /><Summary s={b} curve={curveB} r={rB} win={winner === 'B'} /></div>
      <p className="mt-4 text-xs text-zinc-500">Compounded from a $1,000 start, risking {riskPct}% of equity per trade over {n} trades. Uses the expected value of each strategy — no randomness.</p>
    </Card>
  );
}

/* ================= PRACTICE LOG (existing, server-backed) ================= */
function PracticeLog() {
  const [cats, setCats] = useState([]);
  const [entries, setEntries] = useState([]);
  const [breakdown, setBreakdown] = useState([]);
  const [totalMin, setTotalMin] = useState(0);
  const [loading, setLoading] = useState(true);
  const [minutes, setMinutes] = useState('30');
  const [categoryId, setCategoryId] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [goal, setGoal] = useState(30);
  const load = () => {
    Promise.all([
      api.get('/outside-hours/categories').then((r) => r.data.categories || []),
      api.get('/outside-hours').then((r) => r.data),
      api.get('/progress-summary').then((r) => r.data.daily_goal_minutes).catch(() => 30),
    ]).then(([c, oh, g]) => {
      setCats(c); setCategoryId((p) => p || (c[0] && c[0].category_id) || '');
      setEntries(oh.entries || []); setBreakdown(oh.breakdown || []); setTotalMin(oh.total_minutes || 0); setGoal(g || 30); setLoading(false);
    });
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps
  const addEntry = async (e) => {
    e.preventDefault();
    const m = parseInt(minutes, 10);
    if (!m) return toast.error('Enter minutes (non-zero)');
    if (!categoryId) return toast.error('Pick a category');
    setBusy(true);
    try { await api.post('/outside-hours', { minutes: m, category_id: categoryId, note }); setNote(''); setMinutes('30'); toast.success('Logged practice time'); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Could not log time'); } finally { setBusy(false); }
  };
  const del = async (id) => { await api.delete(`/outside-hours/${id}`); load(); };
  const saveGoal = async (val) => { setGoal(val); try { await api.put('/user/daily-goal', { daily_goal_minutes: val }); toast.success('Daily goal updated'); } catch { toast.error('Could not update goal'); } };
  if (loading) return <div className="grid h-[40vh] place-items-center"><Spinner className="h-8 w-8" /></div>;
  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <Card className="p-6 lg:col-span-1">
        <div className="mb-4 flex items-center gap-2 font-heading text-lg font-semibold text-white"><Clock size={18} className="text-emerald" /> Log practice</div>
        <form onSubmit={addEntry} className="space-y-3">
          <div><label className="mb-1 block text-xs text-zinc-500">Minutes</label><Input type="number" value={minutes} onChange={(e) => setMinutes(e.target.value)} data-testid="oh-minutes-input" /></div>
          <div><label className="mb-1 block text-xs text-zinc-500">Category</label>
            <div className="flex flex-wrap gap-2">{cats.map((c) => (
              <button type="button" key={c.category_id} onClick={() => setCategoryId(c.category_id)} data-testid={`oh-cat-${c.category_id}`}
                className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${categoryId === c.category_id ? 'border-white/0 bg-white text-black' : 'border-white/10 text-zinc-400 hover:border-white/30'}`}>{c.name}</button>
            ))}</div>
          </div>
          <Textarea rows={2} placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="oh-note-input" />
          <Button type="submit" variant="emerald" className="w-full" disabled={busy} data-testid="oh-submit"><Plus size={16} /> Log time</Button>
        </form>
        <div className="mt-6 border-t border-white/5 pt-5">
          <div className="mb-2 flex items-center gap-2 text-sm text-zinc-400"><Target size={15} className="text-emerald" /> Daily goal: <span className="tnum text-white">{goal}m</span></div>
          <div className="flex flex-wrap gap-2">{[15, 30, 45, 60, 90].map((v) => (
            <button key={v} onClick={() => saveGoal(v)} data-testid={`goal-${v}`} className={`rounded-full border px-3 py-1 text-xs transition-colors ${goal === v ? 'border-emerald bg-emerald/15 text-emerald' : 'border-white/10 text-zinc-400 hover:border-white/30'}`}>{v}m</button>
          ))}</div>
        </div>
      </Card>
      <div className="space-y-6 lg:col-span-2">
        <Card className="p-6">
          <div className="flex items-baseline justify-between"><span className="text-sm text-zinc-500">Total outside practice</span><span className="tnum text-3xl font-bold text-white">{(totalMin / 60).toFixed(1)}h</span></div>
          {breakdown.length > 0 && (<div className="mt-4 space-y-2">{breakdown.map((bd) => { const pct = totalMin ? Math.round((bd.minutes / totalMin) * 100) : 0; return (
            <div key={bd.category_id}><div className="mb-1 flex justify-between text-xs"><span className="text-zinc-400">{bd.name}</span><span className="tnum text-zinc-500">{fmtMinutes(bd.minutes)}</span></div><div className="h-1.5 w-full rounded-full bg-white/10"><div className="h-full rounded-full bg-emerald" style={{ width: `${pct}%` }} /></div></div>
          ); })}</div>)}
        </Card>
        <Card className="p-6">
          <h2 className="mb-4 font-heading text-lg font-semibold text-white">Recent entries</h2>
          {entries.length === 0 ? <p className="text-sm text-zinc-500">No entries yet.</p> : (<div className="space-y-2">{entries.map((e) => (
            <div key={e.entry_id} className="flex items-center gap-4 rounded-xl border border-white/5 bg-ink-surface px-4 py-3" data-testid={`oh-entry-${e.entry_id}`}>
              <div className="tnum w-16 text-lg font-bold text-white">{e.minutes}m</div>
              <div className="min-w-0 flex-1"><div className="text-sm text-white">{e.category?.name || e.category_name_snapshot || 'Practice'}</div>{e.note && <div className="truncate text-xs text-zinc-500">{e.note}</div>}</div>
              <div className="text-xs text-zinc-600">{timeAgo(e.logged_at)}</div>
              <button onClick={() => del(e.entry_id)} data-testid={`oh-delete-${e.entry_id}`} className="text-zinc-600 hover:text-red-400"><Trash2 size={16} /></button>
            </div>
          ))}</div>)}
        </Card>
      </div>
    </div>
  );
}

/* ================= MARKET MOVERS (existing, server-backed) ================= */
function fmtNum(n) { const x = Number(n); if (!isFinite(x)) return String(n ?? '—'); if (Math.abs(x) >= 1e9) return (x / 1e9).toFixed(2) + 'B'; if (Math.abs(x) >= 1e6) return (x / 1e6).toFixed(2) + 'M'; if (Math.abs(x) >= 1e3) return (x / 1e3).toFixed(1) + 'K'; return x.toLocaleString(); }
const pick = (o, keys) => { for (const k of keys) if (o[k] !== undefined && o[k] !== null) return o[k]; return undefined; };
function MarketMovers() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const load = () => {
    setLoading(true); setErr(null);
    Promise.all([
      api.get('/market/price-volume-leaders').then((r) => r.data),
      api.get('/market/price-volume-leaders/status').then((r) => r.data).catch(() => null),
    ]).then(([data, st]) => {
      const list = data.leaders || data.items || data.results || data.data || (Array.isArray(data) ? data : []);
      setRows(Array.isArray(list) ? list : []); setStatus(st);
    }).catch((e) => setErr(e?.response?.data?.detail || 'Market data unavailable')).finally(() => setLoading(false));
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (loading) return <div className="grid h-[40vh] place-items-center"><Spinner className="h-8 w-8" /></div>;
  return (
    <Card className="p-6" data-testid="market-movers">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2 font-heading text-lg font-semibold text-white"><LineChart size={18} className="text-emerald" /> Price / Volume leaders</div>
        <div className="flex items-center gap-3">{status?.updated_at && <span className="text-xs text-zinc-500">Updated {timeAgo(status.updated_at)}</span>}<Button size="sm" variant="outline" onClick={load} data-testid="market-refresh"><RefreshCw size={14} /> Refresh</Button></div>
      </div>
      {err ? <div className="py-10 text-center text-sm text-zinc-500">{err}</div> : rows.length === 0 ? <div className="py-10 text-center text-sm text-zinc-500">No market movers right now.</div> : (
        <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="border-b border-white/10 text-left text-xs uppercase tracking-wide text-zinc-500">
          <th className="py-2 pr-4">Symbol</th><th className="py-2 pr-4 text-right">Price</th><th className="py-2 pr-4 text-right">Change</th><th className="py-2 pr-4 text-right">Volume</th><th className="py-2 pr-4 text-right">Rel Vol</th></tr></thead>
          <tbody>{rows.map((r, i) => {
            const sym = pick(r, ['symbol', 'ticker', 'Symbol', 'name']) || '—';
            const price = pick(r, ['price', 'last', 'close', 'Price']);
            const chg = pick(r, ['change_percent', 'changePercent', 'percent_change', 'change_pct', 'change']);
            const vol = pick(r, ['volume', 'Volume', 'vol']); const rvol = pick(r, ['relative_volume', 'rel_volume', 'rvol']);
            const cn = Number(chg); const up = isFinite(cn) ? cn >= 0 : true;
            return (<tr key={r.symbol || r.ticker || i} className="border-b border-white/5" data-testid={`market-row-${sym}`}>
              <td className="py-3 pr-4 font-medium text-white">{sym}</td>
              <td className="py-3 pr-4 text-right tnum text-zinc-200">{price !== undefined ? `$${fmtNum(price)}` : '—'}</td>
              <td className={`py-3 pr-4 text-right tnum ${up ? 'text-emerald' : 'text-red-400'}`}>{chg !== undefined ? <span className="inline-flex items-center gap-1">{up ? <TrendingUp size={13} /> : <TrendingDown size={13} />}{isFinite(cn) ? `${cn.toFixed(2)}%` : String(chg)}</span> : '—'}</td>
              <td className="py-3 pr-4 text-right tnum text-zinc-400">{vol !== undefined ? fmtNum(vol) : '—'}</td>
              <td className="py-3 pr-4 text-right tnum text-zinc-400">{rvol !== undefined ? fmtNum(rvol) : '—'}</td>
            </tr>);
          })}</tbody></table></div>
      )}
    </Card>
  );
}

/* ================= VANQUISH (server-driven promo) ================= */
function Vanquish() {
  const [v, setV] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get('/settings/vanquish').then((r) => setV(r.data)).catch(() => setV(null)).finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="grid h-[40vh] place-items-center"><Spinner className="h-8 w-8" /></div>;
  const active = v && v.enabled && v.url;
  return (
    <Card className="mx-auto max-w-xl overflow-hidden p-0" data-testid="vanquish-card">
      <div className="grain relative p-8 text-center" style={{ background: 'linear-gradient(160deg,#062a02,#000000)' }}>
        <div className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-2xl bg-emerald/15 text-emerald"><Zap size={26} /></div>
        {v?.discount_label && <div className="mb-2 inline-block rounded-full border border-emerald/40 bg-emerald/10 px-3 py-1 text-xs font-bold uppercase tracking-widest text-emerald" data-testid="vanquish-discount">{v.discount_label}</div>}
        <h2 className="font-heading text-2xl font-extrabold text-white" data-testid="vanquish-title">{v?.title || 'Vanquish'}</h2>
        {v?.subtitle && <p className="mx-auto mt-2 max-w-sm text-sm text-zinc-400" data-testid="vanquish-subtitle">{v.subtitle}</p>}
        <div className="mt-6">
          {active ? (
            <a href={v.url} target="_blank" rel="noreferrer" data-testid="vanquish-cta"
              className="inline-flex items-center gap-2 rounded-full bg-emerald px-7 py-3 text-sm font-semibold text-black transition-transform hover:-translate-y-0.5">
              {v.cta_label || 'Claim offer'} <Zap size={16} />
            </a>
          ) : (
            <span className="inline-block rounded-full border border-white/10 px-6 py-3 text-sm text-zinc-500" data-testid="vanquish-inactive">
              This offer isn't active right now.
            </span>
          )}
        </div>
      </div>
    </Card>
  );
}

/* ================= TOOLS PAGE ================= */
const TABS = [
  ['position', 'Position Size', Ruler],
  ['ev', 'EV Calculator', Calculator],
  ['market', 'Market Movers', LineChart],
];
export default function Tools() {
  const [tab, setTab] = useState('position');
  return (
    <div className="space-y-6" data-testid="tools-page">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-widest text-emerald">Tools</p>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-white">Trading tools</h1>
        <p className="mt-1 text-sm text-zinc-500">Quick utilities to sanity-check your setups and process.</p>
      </div>
      <div className="flex gap-1 overflow-x-auto border-b border-white/10">
        {TABS.map(([key, label, Icon]) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`tools-tab-${key}`}
            className={`flex shrink-0 items-center gap-2 border-b-2 px-4 py-3 text-sm transition-colors ${tab === key ? 'border-emerald text-white' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>
      {tab === 'position' && <PositionSizeCalculator />}
      {tab === 'ev' && <EVCalculator />}
      {tab === 'market' && <MarketMovers />}
    </div>
  );
}
